import { AfterViewInit, Attribute, Directive, ElementRef, Renderer2 } from '@angular/core';

import { qaElements } from './qa-elements.consts';

/**
 * @description
 * Directive to create QA ID `data-qa` on element/component either by:
 * - automatically building (best effort) and attaching it from matched data available (inner tags/attributes/angular attributes)
 *   from attributes defined in qa-elements.consts.ts
 * - or by mannually passing in the string QA ID to set `rxQaId="<element-id>"`  (use only if needed)
 *
 * @usageNotes
 * The directive module requires to be imported in the consuming module.
 * If a new component is introduced and need QA ID
 * - add to the selector
 * - create a new entry in the map in qa-elements.consts.ts
 */
@Directive({
  selector: `rx-button,rx-input,rx-autocomplete,rx-datepicker-range,
             rx-select,rx-room-select,rx-checkboxes,rx-map-view-thumb,
             rx-radio,rx-guest-details-form-validation,rx-textarea,
             rx-label-value-card,rx-card-button,rx-date-card,rx-alert,
             [rx-error],[rxQaId],
             rx-login a,li>a.dropdown-item,
             div[aria-controls],:not(rx-button)>button.btn`,
  standalone: true,
})
export class QaIdDirective implements AfterViewInit {
  labels: Set<string>;

  constructor(
    @Attribute('rxQaId') public rxQaId = '',
    private el: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    const nativeEl = this.el.nativeElement;
    // nativeEl?.getAttribute('data-qa')?.length > 0
    if (!this.rxQaId) {
      const rootTag: string = nativeEl.tagName.toLowerCase();
      const currentElement = qaElements[rootTag];

      // exit if component is not identified
      if (!currentElement) {
        return;
      }

      this.labels = new Set();
      const { customAttrs, nativeAttrs, innerElTag } = currentElement;

      if (customAttrs && customAttrs.length > 0) {
        const ngAttrValue = this.getRootElAttrValue(nativeEl, customAttrs);
        this.addLabel(ngAttrValue);
      }

      const innerEl = nativeEl.querySelector(innerElTag);

      if (nativeAttrs && nativeAttrs.length > 0) {
        let innerTagAttrValue;
        if (innerEl) {
          innerTagAttrValue = this.getInnerElAttrValue(innerEl, nativeAttrs);
          this.addLabel(innerEl.tagName);
        } else {
          innerTagAttrValue = this.getRootElAttrValue(nativeEl, nativeAttrs);
          this.addLabel(nativeEl.tagName);
        }
        this.addLabel(innerTagAttrValue);
      }

      this.rxQaId = [...this.labels].join('-');

      // buttons/anchors have limited information so we take the content
      if (['button', 'rx-button', 'a'].includes(rootTag)) {
        const text = nativeEl.textContent || nativeEl.innerText;
        if (text) {
          const parsedText = text.trim().toLowerCase();
          this.rxQaId = `${parsedText}-${this.rxQaId}`;
        }
      }

      if (['div', 'rx-guest-details-validation-messages'].includes(rootTag) && nativeEl.hasAttribute('rx-error')) {
        this.rxQaId += '-error';
      }
    }

    this.renderer.setAttribute(nativeEl, 'data-qa', this.rxQaId.replace(/\s+/g, '-'));
    this.renderer.removeAttribute(nativeEl, 'rxQaId');
  }

  addLabel(label: string) {
    if (label) {
      this.labels.add(label.trim().toLowerCase());
    }
  }

  /**
   * @description root element attributes from ElementRef are accessed using getAttribute
   * @param rootEl root element from ElementRef
   * @param attrs attributes to match on the element
   * @returns matched element attribute value, otherwise ''
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRootElAttrValue(rootEl: any, attrs: string[]) {
    return rootEl.getAttribute(attrs.find((attr: string) => rootEl.getAttribute(attr)) || '') || '';
  }

  /**
   * @description inner element attributes using querySelector are accessing using []
   * @param innerEl inner element
   * @param attrs attributes to match on the element
   * @returns matched element attribute value, otherwise ''
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getInnerElAttrValue(innerEl: any, attrs: string[]) {
    return innerEl[attrs.find((attr: string) => innerEl[attr]) || ''] || '';
  }
}
